import {StateCreator} from 'zustand';
import {List, Map} from 'immutable';
import {AssignmentModelV3, DraftAssignmentModelV1} from '@albert-io/models';
import sessionStore from 'client/Session/SessionStore';

import {getAssignmentQuery} from 'client/components/ContentDiscovery/AssignmentSettings/AssignmentSettings.queries';
import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

import type {SelectedRecipients} from '../../Document/Settings/RecipientSettings/RecipientSettings.types';

type AssignmentState = {
  assignment: AssignmentModelV3 | DraftAssignmentModelV1 | null;
  assignmentLoading: boolean;
  assignmentResults: List<AssignmentModelV3 | DraftAssignmentModelV1>;
  selectedRecipients: SelectedRecipients; // recipients to display count on confirmation/share list
  isReassign: boolean;
  classrooms: List<any>;
  isDirty?: boolean;
  isNavigatingAway?: boolean;
};

type AssignmentActions = {
  getAssignmentById: (assignmentId: string) => void;
  setAssignmentResults: (assignmentResults: List<AssignmentModelV3>) => void;
  setSelectedRecipients: (selectedRecipients: SelectedRecipients) => void;
  reset: () => void;
  setIsReassign: () => void;
  setClassrooms: (classrooms: List<any>) => void;
  setIsDirty: (isDirty: boolean) => void;
  setIsNavigatingAway: (isNavigatingAway: boolean) => void;
};

const initialState: AssignmentState = {
  assignment: null,
  assignmentLoading: false,
  assignmentResults: List(),
  selectedRecipients: Map(),
  isReassign: false,
  classrooms: List(),
  isDirty: false,
  isNavigatingAway: false
};

export type AssignmentSlice = AssignmentState & AssignmentActions;

/* eslint-disable no-param-reassign */
export const createAssignmentSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  AssignmentSlice
> = (set, get): AssignmentSlice => ({
  ...initialState,
  // initial fetch of subjects by curriculum areas along with personalized and classroom subjects
  getAssignmentById: async (assignmentId) => {
    if (!assignmentId) {
      return;
    }

    set((state) => {
      state.assignment.assignmentLoading = true;
    });
    try {
      const {isDraft} = get().draft;
      const [fetchedAssignment] = await Promise.all([
        getAssignmentQuery(assignmentId, sessionStore.isSchoolAdministrator(), isDraft)
      ]);
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = fetchedAssignment;
      });
    } catch (error) {
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = null;
      });
      throw error;
    }
  },

  setAssignmentResults: (assignmentResults) =>
    set((state) => {
      state.assignment.assignmentResults = assignmentResults;
    }),

  setSelectedRecipients: (selectedRecipients) =>
    set((state) => {
      state.assignment.selectedRecipients = selectedRecipients;
    }),

  setIsReassign: () =>
    set((state) => {
      state.assignment.isReassign = true;
    }),

  setClassrooms: (classrooms) =>
    set((state) => {
      state.assignment.classrooms = classrooms;
    }),

  setIsDirty: (isDirty) =>
    set((state) => {
      state.assignment.isDirty = isDirty;
    }),

  setIsNavigatingAway: (isNavigatingAway) =>
    set((state) => {
      state.assignment.isNavigatingAway = isNavigatingAway;
    }),

  reset: () =>
    set((state) => {
      state.assignment = {...state.assignment, ...initialState};
    })
});
